import { useState, useCallback, useEffect } from 'react';
import DatePicker from '../../components/DatePicker';
import RezervasyonDetayModal from '../../components/RezervasyonDetayModal';
import UsageInfo from '../../components/UsageInfo';
import { getFiyatAnalizi } from '../../services/api';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import { useKasa } from '../../context/KasaContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';

// ── Helpers ──────────────────────────────────────────────────
function pad(n) { return String(n).padStart(2, '0'); }
function today() { const d = new Date(); return [pad(d.getDate()), pad(d.getMonth()+1), d.getFullYear()].join('.'); }
function nextMonth() { const d = new Date(); d.setDate(d.getDate()+31); return [pad(d.getDate()), pad(d.getMonth()+1), d.getFullYear()].join('.'); }
function parseDate(str) {
  if (!str) return null;
  const [g, a, y] = str.split('.').map(Number);
  if (!g || !a || !y) return null;
  return new Date(y, a - 1, g);
}
function ddToIso(s) { if (!s) return ''; const p = s.split('.'); return p[2]+'-'+p[1]+'-'+p[0]; }
function fmt(n) { return Number(n ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 }); }

const DURUM_CONFIG = {
  'Check IN Bekliyor': { bg: 'bg-amber-100/20 text-amber-600 border-amber-200/30', dot: 'bg-amber-400' },
  'C-IN Yapıldı':      { bg: 'bg-emerald-100/20 text-emerald-600 border-emerald-200/30', dot: 'bg-emerald-400' },
  'C-OUT Yapıldı':     { bg: 'bg-blue-100/20 text-blue-600 border-blue-200/30', dot: 'bg-blue-400' },
  'İptal':             { bg: 'bg-red-100/20 text-red-600 border-red-200/30', dot: 'bg-red-400' },
  'No-show':           { bg: 'bg-orange-100/20 text-orange-600 border-orange-200/30', dot: 'bg-orange-400' },
};

function DurumBadge({ durum }) {
  const c = DURUM_CONFIG[durum] || { bg: 'bg-gray-100/20 text-gray-500 border-gray-200/30', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all ${c.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} shadow-sm animate-pulse`} />
      {durum}
    </span>
  );
}

function ColFilter({ value, onChange, placeholder }) {
  return (
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full text-[10px] font-bold border-2 border-transparent bg-gray-50/50 hover:bg-white focus:bg-white focus:border-emerald-300 rounded-xl px-3 py-2 outline-none transition-all placeholder-gray-300 shadow-inner" />
  );
}

function SortIcon({ col, sortK, sortD }) {
  if (sortK !== col) return <span className="text-gray-300 ml-1 text-xs transition-opacity opacity-50">⇅</span>;
  return <span className="text-emerald-500 ml-1 text-xs animate-bounce-subtle">{sortD === 'asc' ? '↑' : '↓'}</span>;
}

function StatCard({ icon, label, value, sub, bgClass, textClass }) {
  return (
    <div className={`relative overflow-hidden rounded-[2.5rem] p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group ${bgClass} border border-white/40 shadow-lg transform-gpu`}>
      <div className="absolute -right-4 -bottom-4 text-7xl opacity-5 group-hover:scale-125 group-hover:-rotate-12 transition-transform duration-500 will-change-transform">{icon}</div>
      <div className="flex flex-col h-full justify-between relative z-10">
        <div className="flex items-start justify-between mb-2">
          <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${textClass}`}>{label}</span>
          <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl bg-white/80 backdrop-blur-md shadow-sm font-emoji">{icon}</span>
        </div>
        <div className="mt-2 text-2xl font-black text-gray-900 tracking-tighter leading-none truncate">{value}</div>
        {sub && <p className={`text-[9px] font-black mt-3 tracking-widest uppercase opacity-80 ${textClass}`}>{sub}</p>}
      </div>
    </div>
  );
}

export default function GunlereGoreFiyat() {
  const { paraBirimleri } = useKasa();
  const paraBirimiListesi = (paraBirimleri || []).map(p => p.tcmbKod || p.ad).filter(Boolean);

  const [giris,       setGiris]       = useState(today());
  const [cikis,       setCikis]       = useState(nextMonth());
  const [odaNo,       setOdaNo]       = useState('');
  const [adSoyad,     setAdSoyad]     = useState('');
  const [para,        setPara]        = useState('Hepsi');
  const [aktifFiltre, setAktifFiltre] = useState(null);

  // Kolon filtreleri
  const [kolF, setKolF] = useState({ rezNo: '', misafir: '', oda: '', giris: '', cikis: '', gece: '', gunluk: '', toplam: '' });
  const setKol = (k, v) => setKolF(f => ({ ...f, [k]: v }));
  const kolFiltreVar = Object.values(kolF).some(Boolean);

  // Sıralama
  const [sortK, setSortK] = useState('girisTarihi');
  const [sortD, setSortD] = useState('asc');
  function sort(k) { if (sortK === k) setSortD(d => d === 'asc' ? 'desc' : 'asc'); else { setSortK(k); setSortD('asc'); } }

  // Detay modal
  const [detayId, setDetayId] = useState(null);

  // API state
  const [satirlar,    setSatirlar]    = useState([]);
  const [toplamKayit, setToplamKayit] = useState(0);
  const [toplamSayfa, setToplamSayfa] = useState(1);
  const [sayfa,       setSayfa]       = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);

  const fetchData = useCallback(async (f, s) => {
    setLoading(true);
    setError(null);
    try {
      const filtre = f ?? aktifFiltre;
      const params = {
        baslangic:   ddToIso(filtre?.giris  ?? giris),
        bitis:       ddToIso(filtre?.cikis  ?? cikis),
        odaNo:       filtre?.odaNo,
        adSoyad:     filtre?.adSoyad,
        paraBirimi:  filtre?.para,
        sayfa:       s ?? sayfa,
        sayfaBoyutu: 200,
      };
      const res = await getFiyatAnalizi(params);
      if (res) {
        setSatirlar(res.satirlar ?? []);
        setToplamKayit(res.toplamKayit ?? 0);
        setToplamSayfa(res.toplamSayfa ?? 1);
      }
    } catch {
      setError('Veriler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [aktifFiltre, sayfa, giris, cikis]);

  useEffect(() => { fetchData(null, 1); }, []); // eslint-disable-line
  useEffect(() => { if (aktifFiltre === null) return; fetchData(aktifFiltre, 1); }, [aktifFiltre]); // eslint-disable-line
  useEffect(() => { if (sayfa === 1) return; fetchData(aktifFiltre, sayfa); }, [sayfa]); // eslint-disable-line

  function handleGirisChange(val) {
    setGiris(val);
    const g = parseDate(val), c = parseDate(cikis);
    if (g && c && g >= c) {
      const nc = new Date(g); nc.setDate(nc.getDate() + 1);
      setCikis([pad(nc.getDate()), pad(nc.getMonth()+1), nc.getFullYear()].join('.'));
    }
  }
  function handleFiltrele() {
    setSayfa(1);
    setAktifFiltre({ giris, cikis, odaNo, adSoyad, para });
  }
  function handleTemizle() {
    const g = today(), c = nextMonth();
    setGiris(g); setCikis(c); setOdaNo(''); setAdSoyad(''); setPara('Hepsi');
    setSayfa(1);
    setKolF({ rezNo: '', misafir: '', oda: '', giris: '', cikis: '', gece: '', gunluk: '', toplam: '' });
    setAktifFiltre(null);
    fetchData({ giris: g, cikis: c, odaNo: '', adSoyad: '', para: 'Hepsi' }, 1);
  }

  const goruntulenen = satirlar
    .filter(r => {
      if (kolF.rezNo   && !r.rezNo?.includes(kolF.rezNo)) return false;
      if (kolF.misafir && !r.misafirAdlari?.toLocaleLowerCase('tr-TR').includes(kolF.misafir.toLocaleLowerCase('tr-TR'))) return false;
      if (kolF.oda     && !r.odaNo?.includes(kolF.oda)) return false;
      if (kolF.giris   && !r.girisTarihi?.includes(kolF.giris)) return false;
      if (kolF.cikis   && !r.cikisTarihi?.includes(kolF.cikis)) return false;
      if (kolF.gece    && !String(r.geceSayisi).includes(kolF.gece)) return false;
      if (kolF.toplam  && !String(r.toplamTutar).includes(kolF.toplam)) return false;
      if (kolF.gunluk  && !String(r.gunlukFiyat).includes(kolF.gunluk)) return false;
      return true;
    })
    .slice()
    .sort((a, b) => {
      let av = a[sortK] ?? '', bv = b[sortK] ?? '';
      if (['odaTutari','toplamTutar','alinan','kalan','gunlukFiyat'].includes(sortK)) {
        av = parseFloat(av) || 0; bv = parseFloat(bv) || 0;
      }
      if (av < bv) return sortD === 'asc' ? -1 : 1;
      if (av > bv) return sortD === 'asc' ? 1 : -1;
      return 0;
    });

  const aktifler    = goruntulenen.filter(r => r.durum !== 'İptal' && r.durum !== 'No-show');
  const toplamGece  = goruntulenen.reduce((s, r) => s + (r.geceSayisi ?? 0), 0);
  const toplamTutar = aktifler.reduce((s, r) => s + (r.toplamTutar ?? 0), 0);
  const toplamKalan = aktifler.reduce((s, r) => s + (r.kalan ?? 0), 0);
  const ortGunluk   = aktifler.length > 0
    ? aktifler.reduce((s, r) => s + (r.gunlukFiyat ?? 0), 0) / aktifler.length
    : 0;

  function excelExport() {
    const baslik = ['Rez.No','Misafirler','Oda','Oda Tipi','Giriş','G.Saat','Çıkış','Ç.Saat','Gece','Oda Tutarı','Günlük','Adisyon','Toplam','Alınan','Kalan','PB','Durum','Acente'];
    const satirlar = goruntulenen.map(r => [
      r.rezNo, r.misafirAdlari, r.odaNo, r.odaTipiAd,
      r.girisTarihi, r.girisSaati, r.cikisTarihi, r.cikisSaati,
      r.geceSayisi, r.odaTutari, r.gunlukFiyat, r.toplamAdisyon,
      r.toplamTutar, r.alinan, r.kalan, r.paraBirimi, r.durum, r.acenteAd,
    ]);
    downloadExcel('fiyat-analizi', 'Fiyat Analizi', baslik, satirlar);
  }

  function yazdir() {
    const baslik = ['Rez.No','Misafirler','Oda','Giriş','Çıkış','Gece','Oda Tutarı','Toplam','Alınan','Kalan','PB','Durum'];
    const satirlar = goruntulenen.map(r => [r.rezNo, r.misafirAdlari, r.odaNo, r.girisTarihi, r.cikisTarihi, r.geceSayisi, r.odaTutari, r.toplamTutar, r.alinan, r.kalan, r.paraBirimi, r.durum]);
    printTable('Günlere Göre Fiyat Analizi', baslik, satirlar, goruntulenen.length + ' rezervasyon');
  }

  const cols = [
    { key: 'rezNo',       label: 'Rez. No',   fKey: 'rezNo'   },
    { key: 'misafirAdlari', label: 'Misafirler', fKey: 'misafir' },
    { key: 'odaNo',       label: 'Oda',       fKey: 'oda'     },
    { key: 'girisTarihi', label: 'Giriş',     fKey: 'giris'   },
    { key: 'cikisTarihi', label: 'Çıkış',     fKey: 'cikis'   },
    { key: 'geceSayisi',  label: 'Gece',      fKey: 'gece'    },
    { key: 'gunlukFiyat', label: 'Günlük ₺',  fKey: 'gunluk'  },
    { key: 'toplamTutar', label: 'Toplam ₺',  fKey: 'toplam'  },
    { key: 'paraBirimi',  label: 'PB',        fKey: null      },
    { key: 'durum',       label: 'Durum',     fKey: null      },
    { key: 'acenteAd',    label: 'Acente',    fKey: null      },
    { key: 'detay',       label: 'İncele',    fKey: null      },
  ];

  return (
    <div className="p-3 md:p-6 space-y-8 min-h-screen bg-gray-50/50 transform-gpu animate-fade-in">

      {/* Premium Header */}
      <div className="relative overflow-hidden bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-xl p-4 md:p-8 transform-gpu">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-emerald-100 rounded-full blur-[80px] opacity-50 will-change-transform"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-teal-100 rounded-full blur-[80px] opacity-40 will-change-transform"></div>
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="flex items-center gap-6 min-w-0">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 via-teal-600 to-green-700 rounded-2xl flex-shrink-0 flex items-center justify-center text-white text-3xl shadow-2xl shadow-emerald-200 transform-gpu hover:scale-105 transition-transform duration-500">💰</div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tighter truncate">Gelişmiş Fiyat Analizi</h1>
              <p className="text-sm font-bold text-gray-400 mt-1 uppercase tracking-widest truncate">Geçmiş Ve Gelecek Tarihli Gelir & Fiyatlandırma Raporu</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <button onClick={excelExport} className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-white/80 backdrop-blur-md border-2 border-emerald-100/50 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 font-black px-6 py-4 rounded-2xl shadow-lg shadow-emerald-50 transition-all text-[10px] uppercase tracking-widest hover:-translate-y-1 active:scale-95 whitespace-nowrap transform-gpu">
              <span>📊</span> EXCEL'E AKTAR
            </button>
            <button onClick={yazdir} className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-white/80 backdrop-blur-md border-2 border-gray-100/50 hover:border-gray-800 hover:bg-gray-800 hover:text-white text-gray-700 font-black px-6 py-4 rounded-2xl shadow-lg shadow-gray-50 transition-all text-[10px] uppercase tracking-widest hover:-translate-y-1 active:scale-95 whitespace-nowrap transform-gpu">
              <span>🖨️</span> YAZDIR
            </button>
          </div>
        </div>
      </div>

      {/* Hata */}
      {error && (
        <div className="bg-red-50/80 backdrop-blur-md border border-red-200/50 text-red-700 rounded-2xl px-5 py-4 text-sm font-bold shadow-lg shadow-red-50 transform-gpu animate-shake">
          ⚠️ {error}
        </div>
      )}

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-5 relative z-10 transform-gpu">
        <StatCard icon="📋" label="Toplam Rezervasyon" value={loading ? '…' : toplamKayit} sub={`${toplamGece} Gece Toplamda`} bgClass="bg-gradient-to-br from-blue-50/50 to-indigo-50/50" textClass="text-blue-600" />
        <StatCard icon="💰" label="Beklenen Toplam Gelir" value={loading ? '…' : `${fmt(toplamTutar)} TL`} sub="Tüm Gecelemeler Kaydı" bgClass="bg-gradient-to-br from-emerald-50/50 to-green-50/50" textClass="text-emerald-600" />
        <StatCard icon="📈" label="ADR (Ortalama Günlük)" value={loading ? '…' : `${fmt(ortGunluk)} TL`} sub="Güncel Oda Başına Gelir" bgClass="bg-gradient-to-br from-purple-50/50 to-fuchsia-50/50" textClass="text-purple-600" />
        <StatCard icon="⚠️" label="Kalan (Onaysız) Bakiye" value={loading ? '…' : `${fmt(toplamKalan)} TL`} sub="Tahsil Edilmesi Gereken Yekün"
          bgClass={toplamKalan > 0 ? "bg-gradient-to-br from-red-50/50 to-rose-50/50" : "bg-gradient-to-br from-gray-50/50 to-slate-50/50"} textClass={toplamKalan > 0 ? "text-red-500" : "text-gray-500"} />
      </div>

      {/* Akıllı Filtre Paneli */}
      <div className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl p-6 md:p-8 z-20 relative transform-gpu">
        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
           <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse shadow-sm" /> Detaylı Sorgulama Aracı
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-3 lg:col-span-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">Giriş Tarihi</label>
            <DatePicker value={giris} onChange={handleGirisChange} />
          </div>
          <div className="md:col-span-3 lg:col-span-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">Çıkış Tarihi</label>
            <DatePicker value={cikis} onChange={setCikis} minDate={giris} />
          </div>
          <div className="md:col-span-3 lg:col-span-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">Oda No</label>
            <input value={odaNo} onChange={e => setOdaNo(e.target.value)} placeholder="Örn: 204" className="w-full bg-gray-50/50 border-2 border-transparent focus:bg-white focus:border-emerald-500 rounded-2xl px-4 py-3 text-sm font-black text-gray-700 transition-all outline-none placeholder-gray-300" />
          </div>
          <div className="md:col-span-3 lg:col-span-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">Ad Soyad</label>
            <input value={adSoyad} onChange={e => setAdSoyad(e.target.value)} placeholder="Misafir ara..." className="w-full bg-gray-50/50 border-2 border-transparent focus:bg-white focus:border-emerald-500 rounded-2xl px-4 py-3 text-sm font-black text-gray-700 transition-all outline-none placeholder-gray-300" />
          </div>
          <div className="md:col-span-4 lg:col-span-2">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2.5 ml-1">Para Birimi</label>
            <select value={para} onChange={e => setPara(e.target.value)} className="w-full bg-gray-50/50 border-2 border-transparent focus:bg-white focus:border-emerald-500 rounded-2xl px-4 py-3 text-sm font-black text-gray-700 outline-none cursor-pointer appearance-none">
              <option value="Hepsi">- Hepsi -</option>
              {paraBirimiListesi.map(pb => (
                <option key={pb} value={pb}>{pb}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2 md:col-span-8 lg:col-span-2 flex gap-3 h-[52px]">
            <button onClick={handleFiltrele} disabled={loading}
              className="flex-[2] bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-[1.25rem] font-black text-[10px] uppercase tracking-widest shadow-xl shadow-emerald-200 transition-all active:scale-95 flex items-center justify-center transform-gpu">
              {loading ? '...' : '🔍 ANALİZ ET'}
            </button>
            <button onClick={handleTemizle}
              className="flex-[1] bg-gray-100/50 hover:bg-gray-200/80 text-gray-500 rounded-[1.25rem] font-black text-[10px] uppercase tracking-widest transition-all transform-gpu active:scale-95">
              SIFIRLA
            </button>
          </div>
        </div>
      </div>

      {/* Tablo – Mobil Kartlar */}
      <div className="md:hidden space-y-3 transform-gpu">
        {loading ? (
          <LoadingSpinner fullPage text="Hesaplamalar Yapılıyor" color="cyan" />
        ) : goruntulenen.length === 0 ? (
          <EmptyState icon="💰" text="İşlem kaydı bulunamadı" />
        ) : goruntulenen.map(r => (
          <div key={r.rezervasyonId} className="bg-white/80 backdrop-blur-xl border-2 border-white/40 rounded-[2rem] p-5 shadow-xl cursor-pointer hover:-translate-y-1 transition-all transform-gpu"
            onClick={() => setDetayId(r.rezervasyonId)}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className="text-[10px] font-black text-indigo-400 tracking-widest uppercase">REZ NO: {r.rezNo}</span>
                <p className="font-black text-gray-900 text-base leading-none mt-1 tracking-tight">{r.misafirAdlari || 'İsimsiz Misafir'}</p>
              </div>
              <DurumBadge durum={r.durum} />
            </div>
            <div className="grid grid-cols-2 gap-4 text-xs text-gray-700 bg-gray-50/50 p-4 rounded-2xl mb-4 border border-white/50 shadow-inner">
               <div className="space-y-1">
                  <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Otopark / Oda</p>
                  <p className="font-black text-emerald-600 bg-white inline-block px-2 py-0.5 rounded-lg border border-emerald-100 shadow-sm">{r.odaNo}</p>
               </div>
               <div className="space-y-1">
                  <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Konaklama</p>
                  <p className="font-black text-gray-800">{r.geceSayisi} Gece</p>
               </div>
               <div className="space-y-1">
                  <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Giriş / Çıkış</p>
                  <p className="font-bold text-[10px] leading-tight">{r.girisTarihi} - {r.cikisTarihi}</p>
               </div>
               <div className="space-y-1">
                  <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Net Toplam</p>
                  <p className="font-black text-gray-900 text-sm">{fmt(r.toplamTutar)} {r.paraBirimi}</p>
               </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{r.acenteAd || 'Münferit Satış'}</span>
              <button className="bg-emerald-600 text-white font-black text-[10px] px-5 py-2 rounded-xl shadow-lg shadow-emerald-100 uppercase tracking-widest active:scale-95 transition-all">Detay İncele</button>
            </div>
          </div>
        ))}
      </div>

      {/* Tablo – Masaüstü */}
      <div className="hidden md:block bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl overflow-hidden transform-gpu">
        <div className="px-10 py-6 border-b border-gray-100/50 bg-gray-50/30 flex items-center justify-between">
          <div>
            <p className="text-sm font-black text-gray-900 uppercase tracking-tighter">Fiyat & Gelir Dökümü</p>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
              {toplamKayit} Toplam İşlem
              {goruntulenen.length !== satirlar.length ? ` · ${goruntulenen.length} Filtrelenmiş` : ''}
            </p>
          </div>
          {kolFiltreVar && (
            <button onClick={() => setKolF({ rezNo:'', misafir:'', oda:'', giris:'', cikis:'', gece:'', gunluk:'', toplam:'' })}
              className="text-[10px] font-black text-red-500 hover:text-red-700 uppercase tracking-widest px-4 py-2 border border-red-100 rounded-xl hover:bg-red-50 transition-all">✕ Filtreleri Kaldır</button>
          )}
        </div>

        <div className="overflow-x-auto min-h-[400px]">
          {loading ? (
            <LoadingSpinner fullPage text="Veri Setleri Haritalanıyor" color="cyan" />
          ) : (
            <table className="w-full text-sm border-separate border-spacing-0" style={{ minWidth: '1300px' }}>
              <thead>
                <tr className="bg-gray-100/50">
                  {cols.map(col => (
                    <th key={col.key}
                      onClick={!['detay','misafirAdlari','durum','paraBirimi','acenteAd','paraBirimi'].includes(col.key) ? () => sort(col.key) : undefined}
                      className={`text-left py-6 px-4 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50 select-none transition-all ${!['detay','misafirAdlari','durum','paraBirimi','acenteAd'].includes(col.key) ? 'cursor-pointer hover:bg-emerald-50 hover:text-emerald-700' : ''}`}>
                      <div className="flex items-center gap-2">
                        {col.label}
                        {!['detay','misafirAdlari','durum','paraBirimi','acenteAd'].includes(col.key) && (
                          <SortIcon col={col.key} sortK={sortK} sortD={sortD} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
                <tr className="bg-white/50">
                  {cols.map(col => (
                    <td key={col.key} className="px-3 py-3 border-b border-gray-50/50 shadow-inner">
                      {col.fKey ? <ColFilter value={kolF[col.fKey]} onChange={v => setKol(col.fKey, v)} placeholder="Ara..." /> : <div />}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {goruntulenen.length === 0 ? (
                  <EmptyState as="td" colSpan={12} icon="💸" text="Finansal kayıt bulunamadı" />
                ) : goruntulenen.map((r, i) => (
                  <tr key={r.rezervasyonId}
                    className={`border-b border-gray-50 transition-all transform-gpu hover:bg-emerald-50/30 cursor-pointer ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/20'}`}
                    onClick={() => setDetayId(r.rezervasyonId)}>
                    <td className="py-5 px-4 font-black text-[11px] text-gray-400 tracking-widest">{r.rezNo}</td>
                    <td className="py-5 px-4 font-black text-gray-900 text-[11px] max-w-[200px] truncate tracking-tight">{r.misafirAdlari || 'Münferit Misafir'}</td>
                    <td className="py-5 px-4">
                      <span className="bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-xl font-black text-xs border border-emerald-100 shadow-sm">{r.odaNo}</span>
                    </td>
                    <td className="py-5 px-4 text-gray-700 whitespace-nowrap text-[11px] font-bold">
                      {r.girisTarihi} <span className="text-gray-400 font-medium ml-1">({r.girisSaati})</span>
                    </td>
                    <td className="py-5 px-4 text-gray-700 whitespace-nowrap text-[11px] font-bold">
                      {r.cikisTarihi} <span className="text-gray-400 font-medium ml-1">({r.cikisSaati})</span>
                    </td>
                    <td className="py-5 px-4">
                      <span className="bg-slate-100/50 text-slate-700 px-3 py-1 rounded-xl font-black text-[10px] uppercase tracking-widest">{r.geceSayisi} gece</span>
                    </td>
                    <td className="py-5 px-4 font-black text-gray-700 text-right tabular-nums">{fmt(r.gunlukFiyat)}</td>
                    <td className="py-5 px-4 font-black text-gray-900 text-right text-base tabular-nums">{fmt(r.toplamTutar)}</td>
                    <td className="py-5 px-4 text-emerald-500 font-black text-[10px] tracking-widest">{r.paraBirimi}</td>
                    <td className="py-5 px-4"><DurumBadge durum={r.durum} /></td>
                    <td className="py-5 px-4 text-gray-400 text-[10px] font-black uppercase tracking-widest max-w-[140px] truncate">{r.acenteAd || 'H/F DİREKT'}</td>
                    <td className="py-5 px-4" onClick={e => { e.stopPropagation(); setDetayId(r.rezervasyonId); }}>
                      <button className="bg-white/80 backdrop-blur-md border border-gray-200 hover:border-emerald-600 hover:bg-emerald-600 hover:text-white text-[10px] font-black px-5 py-2.5 rounded-2xl shadow-sm transition-all uppercase tracking-widest active:scale-95 transform-gpu">Detay</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {goruntulenen.length > 0 && (
                <tfoot>
                  <tr className="bg-emerald-600 text-white font-black text-base shadow-2xl">
                    <td colSpan={5} className="py-8 px-10 text-[10px] uppercase tracking-[0.3em] opacity-80 border-r border-white/10">
                      FİNANSAL PROJEKSİYON ({goruntulenen.length} KAYIT)
                    </td>
                    <td className="py-8 px-4 text-center border-r border-white/10 uppercase tracking-widest text-[12px]">{toplamGece} TOPLAM GECE</td>
                    <td className="py-8 px-4 text-right border-r border-white/10 text-emerald-100"><span className="text-[8px] block opacity-60 mb-1">ORTALAMA GÜNLÜK</span> {fmt(ortGunluk)} TL</td>
                    <td className="py-8 px-4 text-right border-r border-white/10 text-lg"><span className="text-[8px] block opacity-60 mb-1">TOPLAM REZ. GELİRİ</span> {fmt(toplamTutar)} TL</td>
                    <td className="py-8 px-4 text-xs opacity-50 border-r border-white/10">VALÖR: TL</td>
                    <td colSpan={3} className="py-8 px-4 bg-emerald-700">
                      <span className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-[1.25rem] text-[10px] font-black uppercase tracking-[0.2em] shadow-xl ${toplamKalan > 0 ? 'bg-red-500 text-white animate-pulse' : 'bg-emerald-400 text-emerald-950'}`}>
                        {toplamKalan > 0 ? `${fmt(toplamKalan)} TL ALACAK BAKİYE` : '✓ TÜM TAHSİLATLAR TAMAMLANDI'}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      {toplamSayfa > 1 && (
        <div className="flex items-center justify-center gap-4 py-6 transform-gpu">
          <button onClick={() => setSayfa(s => Math.max(1, s-1))} disabled={sayfa === 1 || loading}
            className="w-12 h-12 rounded-2xl bg-white border-2 border-gray-100 flex items-center justify-center text-gray-400 hover:border-emerald-500 hover:text-emerald-500 disabled:opacity-30 transition-all shadow-sm active:scale-90 font-black">←</button>
          <div className="bg-white px-8 py-3 rounded-2xl border-2 border-gray-100 shadow-sm">
             <span className="text-xs font-black text-gray-400 uppercase tracking-widest">Sayfa</span>
             <strong className="text-emerald-600 text-lg ml-2 font-black leading-none">{sayfa}</strong>
             <span className="text-gray-300 mx-2 text-xl">/</span>
             <span className="text-gray-500 font-bold">{toplamSayfa}</span>
          </div>
          <button onClick={() => setSayfa(s => Math.min(toplamSayfa, s+1))} disabled={sayfa === toplamSayfa || loading}
            className="w-12 h-12 rounded-2xl bg-white border-2 border-gray-100 flex items-center justify-center text-gray-400 hover:border-emerald-500 hover:text-emerald-500 disabled:opacity-30 transition-all shadow-sm active:scale-90 font-black">→</button>
        </div>
      )}

      {detayId && (
        <RezervasyonDetayModal rezervasyonId={detayId} onClose={() => setDetayId(null)} onGuncelle={() => { setDetayId(null); fetchData(null, sayfa); }} />
      )}

      <UsageInfo
        color="emerald"
        description="Fiyat Analizi Raporu, rezervasyonların getirdiği geliri gecelik periyotlara (ADR - Ortalama Günlük Ücret) ayırarak, işletmenin karlılığını ve tahsilatlarını izlemenizi sağlayan finansal karnenizdir. Gerçek zamanlı veri akışı sağlar."
        scenarios={[
          { title: "ADR'ın (Ortalama Gecelik Fiyat) Önemi", content: "Karlılık sadece dolmakla olmaz, 'ne kadara' dolduğunuz da önemlidir! ADR ekranınızın kırmızıya döndüğü haftalar otelin fiyatlama stratejisini veya misafir kitlesini gözden geçirmeniz anlamına gelir. Gelir yönetimi için kritik veridir." },
          { title: "Gelecek Gelir Tahmini Analizi", content: "Arama menüsünden gelecek 3 ayı kapsayan bir aralık seçerseniz, bu kartlarda göreceğiniz değerler henüz kasanıza girmemiş olan 'Beklenen Tahmini Geliri' (Accounts Receivable) temsil edecektir. Nakit akış planınızı buna göre yapın." },
          { title: "Bakiye Taraması & Acil Yönetim", content: "Sayfa altında 'Kalan Bakiye' kolonu dolduğunda veya iptal edilmiş / no-show gibi riskli durumları yakalayıp muhasebeye direkt uyarı geçebilirsiniz. Kaçakları anında tespit edin ve proaktif tahsilat başlatın." }
        ]}
      />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.4s cubic-bezier(.22,1,.36,1) forwards; }
        .animate-bounce-subtle { animation: bounceSubtle 2s infinite; }
        @keyframes bounceSubtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
        .animate-shake { animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both; }
        @keyframes shake { 10%, 90% { transform: translate3d(-1px, 0, 0); } 20%, 80% { transform: translate3d(2px, 0, 0); } 30%, 50%, 70% { transform: translate3d(-4px, 0, 0); } 40%, 60% { transform: translate3d(4px, 0, 0); } }
      `}</style>
    </div>
  );
}
